/**
 * LAS RESPUESTAS DE UN REPORTE, UNA POR FILA (Seguimiento de la ficha, 20-10).
 *
 * Francisco: «aquí no se entiende, necesitas separar mejor la información de los reportes».
 * Cada pregunta en su fila (la pregunta en gris, la respuesta debajo en negro y, si la
 * tiene, con su color de Calma), las medidas en una tabla con su nombre y el reporte que el
 * cliente pegó como texto partido en sus preguntas (`lib/textoDelReporte`).
 *
 * Paleta del panel en blanco (la de Clientes): texto #1C1B19, gris #57544E, borde #E5E2DB,
 * superficie #F6F5F2.
 */
import React from 'react';
import { partirElReporte } from '../../lib/textoDelReporte';

/**
 * LA PALETA EN BLANCO PARA LO QUE PINTA CON LOS COLORES DEL TEMA (`bg-card`,
 * `text-foreground`...): la app va en oscuro y esto la pone en claro dentro de un bloque.
 * Se pasa como `style` junto a `bg-white [color-scheme:light]`; los modales van fuera del
 * bloque (se pintan aparte) y la llevan también.
 */
export const TEMA_CLARO = {
    '--background': '0 0% 100%',
    '--foreground': '40 6% 10%',
    '--card': '0 0% 100%',
    '--card-foreground': '40 6% 10%',
    '--popover': '0 0% 100%',
    '--popover-foreground': '40 6% 10%',
    '--muted': '45 20% 96%',
    '--muted-foreground': '40 5% 32%',
    '--border': '42 16% 88%',
    '--input': '42 16% 88%',
};

/** Una pregunta y su respuesta. `tono`: las clases del color de Calma, si lo tiene. */
export const FilaDeRespuesta = ({ pregunta, respuesta, tono = null }) => {
    if (respuesta == null || respuesta === '') return null;
    return (
        <div className="py-1.5 border-b border-[#E5E2DB] last:border-b-0 min-w-0">
            <p className="m-0 text-[#57544E] text-xs leading-snug">{pregunta}</p>
            <p className="m-0 mt-0.5 text-sm text-[#1C1B19] leading-snug whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                {tono
                    ? <span className={`${tono} inline-block rounded px-1.5 py-0.5 font-semibold`}>{respuesta}</span>
                    : respuesta}
            </p>
        </div>
    );
};

/** Las medidas con su nombre, en cm. `filas`: [{key, label, valor, extra?}] (`lib/medidas`). */
export const TablaDeMedidas = ({ filas, titulo = 'Medidas' }) => {
    if (!filas || !filas.length) return null;
    return (
        <div className="min-w-0" data-testid="tabla-de-medidas">
            <p className="m-0 mb-1 text-[#57544E] text-xs">{titulo}</p>
            <table className="w-full text-sm border border-[#E5E2DB] rounded-lg overflow-hidden border-separate [border-spacing:0]">
                <tbody>
                    {filas.map((m, i) => (
                        <tr key={m.key} className={i % 2 ? 'bg-white' : 'bg-[#F6F5F2]'}>
                            <th scope="row" className="px-2.5 py-1 text-left font-normal text-[#57544E]">{m.label}</th>
                            <td className="px-2.5 py-1 text-right tabular-nums font-semibold text-[#1C1B19] whitespace-nowrap">
                                {String(m.valor).replace('.', ',')} cm
                                {m.extra}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Lo que el cliente escribió de su puño. Si es la plantilla del mensual pegada, partida en
 * preguntas y medidas; si no, tal cual, con sus saltos de línea.
 */
export const TextoDelCliente = ({ texto, testId = 'texto-del-cliente' }) => {
    if (!texto) return null;
    const partido = partirElReporte(texto);
    if (!partido) {
        return (
            <p className="m-0 text-sm text-[#1C1B19] whitespace-pre-wrap break-words [overflow-wrap:anywhere]" data-testid={testId}>
                {texto}
            </p>
        );
    }
    return (
        <div className="space-y-2" data-testid={testId}>
            {partido.intro && (
                <p className="m-0 text-sm text-[#1C1B19] whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{partido.intro}</p>
            )}
            {partido.preguntas.length > 0 && (
                <div>
                    {partido.preguntas.map(p => <FilaDeRespuesta key={p.n} pregunta={p.pregunta} respuesta={p.respuesta} />)}
                </div>
            )}
            <TablaDeMedidas filas={partido.medidas} />
        </div>
    );
};
